(function customerReferenceCartModule(globalScope) {
  const ATTRIBUTE_PREFIX = 'customer_references_';
  const ATTRIBUTE_COUNT_KEY = 'customer_references_chunk_count';
  const CUSTOMER_REFERENCE_PROPERTY = 'Kundereference';
  const UNIQUE_ID_PROPERTY = '_unique_id';
  const MAX_ATTRIBUTE_VALUE_LENGTH = 255;
  const MAX_ATTRIBUTE_CHUNKS = 99;

  class CustomerReferenceStorageError extends Error {
    constructor(code) {
      super(code);
      this.name = 'CustomerReferenceStorageError';
      this.code = code;
    }
  }

  function generateUniqueId(lineIndex, existingIds = new Set(), random = Math.random) {
    for (let attempt = 0; attempt < 100; attempt += 1) {
      const randomPart = random().toString(36).substring(7);
      const uniqueId = `${randomPart}_${lineIndex}`;
      if (randomPart && !existingIds.has(uniqueId)) return uniqueId;
    }

    throw new CustomerReferenceStorageError('unique_id_generation_failed');
  }

  function getCustomerReference(item) {
    const customerReference = item.properties?.[CUSTOMER_REFERENCE_PROPERTY];
    if (customerReference == null) return '';
    return String(customerReference).trim();
  }

  function buildReferenceEntries(items) {
    const entries = [];

    for (const item of items) {
      const customerReference = getCustomerReference(item);
      const uniqueId = item.properties?.[UNIQUE_ID_PROPERTY];
      if (customerReference && uniqueId) entries.push([String(uniqueId), customerReference]);
    }

    return entries;
  }

  function packReferenceEntries(
    entries,
    maxValueLength = MAX_ATTRIBUTE_VALUE_LENGTH,
    maxChunks = MAX_ATTRIBUTE_CHUNKS
  ) {
    const chunks = [];
    let currentChunk = {};

    for (const [uniqueId, customerReference] of entries) {
      const candidateChunk = { ...currentChunk, [uniqueId]: customerReference };
      const candidateJson = JSON.stringify(candidateChunk);

      if (candidateJson.length <= maxValueLength) {
        currentChunk = candidateChunk;
        continue;
      }

      if (Object.keys(currentChunk).length === 0) {
        throw new CustomerReferenceStorageError('entry_too_large');
      }

      chunks.push(JSON.stringify(currentChunk));
      currentChunk = { [uniqueId]: customerReference };

      if (JSON.stringify(currentChunk).length > maxValueLength) {
        throw new CustomerReferenceStorageError('entry_too_large');
      }
    }

    if (Object.keys(currentChunk).length > 0) chunks.push(JSON.stringify(currentChunk));
    if (chunks.length > maxChunks) throw new CustomerReferenceStorageError('too_many_chunks');

    return chunks;
  }

  function getChunkAttributeName(index) {
    return `${ATTRIBUTE_PREFIX}${String(index).padStart(3, '0')}`;
  }

  function buildAttributeUpdates(chunks, currentAttributes = {}) {
    const attributes = {
      [ATTRIBUTE_COUNT_KEY]: String(chunks.length)
    };

    chunks.forEach((chunk, index) => {
      attributes[getChunkAttributeName(index + 1)] = chunk;
    });

    for (const attributeName of Object.keys(currentAttributes)) {
      if (
        /^customer_references_\d{3}$/.test(attributeName)
        && !Object.prototype.hasOwnProperty.call(attributes, attributeName)
      ) {
        attributes[attributeName] = '';
      }
    }

    return attributes;
  }

  function attributesNeedUpdate(updates, currentAttributes = {}) {
    return Object.entries(updates).some(
      ([attributeName, value]) => String(currentAttributes[attributeName] ?? '') !== String(value)
    );
  }

  class CartCustomerReferences {
    constructor(cartForm) {
      this.cartForm = cartForm;
      this.cartItems = document.getElementById('cart-items');
      this.errorRegion = document.getElementById('cart-errors');
      this.checkoutControls = Array.from(
        cartForm.querySelectorAll('[name="checkout"], .dynamic-cart-btns')
      );
      this.syncRequested = false;
      this.syncPromise = null;
      this.failed = false;
    }

    sync() {
      this.syncRequested = true;
      if (!this.syncPromise) this.syncPromise = this.runSyncQueue();
      return this.syncPromise;
    }

    async runSyncQueue() {
      this.setBusy(true);

      try {
        while (this.syncRequested) {
          this.syncRequested = false;
          await this.syncOnce();
        }
        this.failed = false;
        this.clearError();
      } catch (error) {
        this.failed = true;
        this.showError(error);
        throw error;
      } finally {
        this.syncPromise = null;
        this.setBusy(this.failed);
      }
    }

    async syncOnce() {
      let cart = await this.fetchCart();
      cart = await this.reconcileUniqueIds(cart);

      const entries = buildReferenceEntries(cart.items);
      const chunks = packReferenceEntries(entries);
      const attributeUpdates = buildAttributeUpdates(chunks, cart.attributes);

      if (attributesNeedUpdate(attributeUpdates, cart.attributes)) {
        await this.updateCartAttributes(attributeUpdates);
      }
    }

    async fetchCart() {
      const response = await fetch(`${theme.routes.cart}.js`, {
        headers: { Accept: 'application/json' }
      });
      return this.parseResponse(response);
    }

    async reconcileUniqueIds(cart) {
      const reservedIds = new Set();
      const retainedIds = new Set();

      for (const item of cart.items) {
        const uniqueId = item.properties?.[UNIQUE_ID_PROPERTY];
        if (uniqueId) reservedIds.add(String(uniqueId));
      }

      for (let index = 0; index < cart.items.length; index += 1) {
        const item = cart.items[index];
        const customerReference = getCustomerReference(item);
        if (!customerReference) continue;

        const currentUniqueId = item.properties?.[UNIQUE_ID_PROPERTY]
          ? String(item.properties[UNIQUE_ID_PROPERTY])
          : '';
        if (currentUniqueId && !retainedIds.has(currentUniqueId)) {
          retainedIds.add(currentUniqueId);
          continue;
        }

        const uniqueId = generateUniqueId(index + 1, reservedIds);
        reservedIds.add(uniqueId);
        retainedIds.add(uniqueId);

        const properties = {
          ...(item.properties || {}),
          [UNIQUE_ID_PROPERTY]: uniqueId
        };
        cart = await this.changeLine(index + 1, item.quantity, properties);
      }

      return cart;
    }

    async changeLine(line, quantity, properties) {
      const response = await fetch(theme.routes.cartChange, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ line, quantity, properties })
      });
      return this.parseResponse(response);
    }

    async updateCartAttributes(attributes) {
      const response = await fetch(theme.routes.cartUpdate, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ attributes })
      });
      return this.parseResponse(response);
    }

    async parseResponse(response) {
      const data = await response.json();
      if (!response.ok) throw new Error(data.errors || data.description || response.status);
      return data;
    }

    setBusy(isBusy) {
      this.cartForm.toggleAttribute('data-customer-reference-syncing', isBusy);

      for (const control of this.checkoutControls) {
        if (control.matches('button')) {
          control.disabled = isBusy;
        } else {
          control.classList.toggle('pointer-events-none', isBusy);
          control.setAttribute('aria-disabled', String(isBusy));
        }
      }
    }

    clearError() {
      if (!this.errorRegion?.dataset.customerReferenceError) return;
      this.errorRegion.textContent = '';
      this.errorRegion.hidden = true;
      delete this.errorRegion.dataset.customerReferenceError;
    }

    showError(error) {
      if (!this.errorRegion) return;

      const isSizeError = error instanceof CustomerReferenceStorageError
        && ['entry_too_large', 'too_many_chunks'].includes(error.code);
      this.errorRegion.textContent = isSizeError
        ? this.cartForm.dataset.customerReferenceSizeError
        : this.cartForm.dataset.customerReferenceSyncError;
      this.errorRegion.hidden = false;
      this.errorRegion.dataset.customerReferenceError = 'true';
    }
  }

  const exported = {
    ATTRIBUTE_COUNT_KEY,
    ATTRIBUTE_PREFIX,
    MAX_ATTRIBUTE_CHUNKS,
    MAX_ATTRIBUTE_VALUE_LENGTH,
    CustomerReferenceStorageError,
    attributesNeedUpdate,
    buildAttributeUpdates,
    buildReferenceEntries,
    generateUniqueId,
    getChunkAttributeName,
    packReferenceEntries
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = exported;

  if (globalScope?.document) {
    globalScope.CartCustomerReferenceUtils = exported;
    document.addEventListener('DOMContentLoaded', () => {
      const cartForm = document.getElementById('cart');
      if (!cartForm) return;

      globalScope.cartCustomerReferences = new CartCustomerReferences(cartForm);
      globalScope.cartCustomerReferences.sync().catch(() => {});
    });
  }
}(typeof window !== 'undefined' ? window : null));
